import React, { useState, useEffect } from 'react';
import QRCode from 'react-qr-code';
import { paymentAPI, customerAPI, configAPI } from '../../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { CreditCard, Printer, Tag } from 'lucide-react';

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

export default function CustomerPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [customer, setCustomer] = useState(null);
  const [config, setConfig]     = useState({ upiId: '', upiId2: '', upiName: 'JagdishNet', upiDiscountPercent: 5 });
  const [showQR, setShowQR]     = useState(false);
  const [selUpi, setSelUpi]     = useState(1);

  useEffect(() => {
    Promise.all([
      paymentAPI.getAll({ limit: 20 }),
      customerAPI.getMe(),
      configAPI.get(),
    ]).then(([pRes, cRes, cfgRes]) => {
      setPayments(pRes.data.data || []);
      setCustomer(cRes.data.data);
      if (cfgRes.data?.data) setConfig(cfgRes.data.data);
    }).catch(() => toast.error('Failed to load data'))
      .finally(() => setLoading(false));
  }, []);

  const discountPct = config.upiDiscountPercent || 0;
  const planPrice   = customer?.catv?.package?.price || customer?.broadband?.plan?.price || 0;
  const upiAmount   = planPrice > 0 ? Math.round(planPrice * (1 - discountPct / 100)) : 0;
  const savings     = planPrice - upiAmount;
  const activeUpiId = selUpi === 1 ? config.upiId : config.upiId2;
  const upiLink     = activeUpiId
    ? `upi://pay?pa=${activeUpiId}&pn=${encodeURIComponent(config.upiName)}&am=${upiAmount}&cu=INR&tn=${encodeURIComponent('CATV Renewal - ' + (customer?.customerId || ''))}`
    : null;

  return (
    <div>
      <h2 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 4 }}>Payments</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '.85rem', marginBottom: 20 }}>Pay your bill or view past payments</p>

      {/* Pay via UPI section */}
      {!loading && customer && planPrice > 0 && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 18, boxShadow: '0 1px 8px rgba(0,0,0,.08)', marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: '.95rem' }}>Pay via UPI</div>
              {discountPct > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 4, fontSize: '.82rem', color: '#16a34a', fontWeight: 600 }}>
                  <Tag size={12} /> {discountPct}% discount — Pay ₹{upiAmount} instead of ₹{planPrice}
                </div>
              )}
            </div>
            <button onClick={() => setShowQR(s => !s)}
              style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: '.88rem' }}>
              {showQR ? 'Hide QR' : 'Show QR Code'}
            </button>
          </div>

          {showQR && upiLink && (
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              {/* Bank selector */}
              {config.upiId2 && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 14, justifyContent: 'center' }}>
                  {[{ n: 1, label: 'HDFC Bank', id: config.upiId }, { n: 2, label: 'ICICI Bank', id: config.upiId2 }].map(({ n, label, id }) => (
                    <button key={n} onClick={() => setSelUpi(n)}
                      style={{ padding: '6px 14px', borderRadius: 8, border: `2px solid ${selUpi === n ? '#4f46e5' : '#d1d5db'}`,
                        background: selUpi === n ? '#eff6ff' : '#fff', cursor: 'pointer', fontSize: '.8rem', fontWeight: 600 }}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
              <div style={{ display: 'inline-block', background: '#fff', padding: 16, borderRadius: 12, border: '2px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,.08)' }}>
                <QRCode value={upiLink} size={200} />
                <div style={{ marginTop: 8, fontSize: '.75rem', color: '#888' }}>{config.upiName} · ₹{upiAmount}</div>
              </div>
              <p style={{ fontSize: '.78rem', color: '#888', marginTop: 8 }}>
                Scan with PhonePe, GPay, Paytm or any UPI app
              </p>
              <div style={{ marginTop: 10, padding: '8px 12px', background: '#fffbeb', borderRadius: 8, border: '1px solid #fde68a', fontSize: '.8rem', color: '#92400e', display: 'inline-block' }}>
                After paying, share the payment screenshot with your ISP for faster update.
              </div>
            </div>
          )}

          {showQR && !upiLink && (
            <p style={{ color: '#888', fontSize: '.85rem', marginTop: 10, textAlign: 'center' }}>
              UPI payment not available. Contact your ISP office.
            </p>
          )}
        </div>
      )}

      {/* Payment history */}
      <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 12 }}>Payment History</h3>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : payments.length === 0 ? (
        <div className="card empty-state" style={{ padding: 48 }}>
          <CreditCard size={32} />
          <h3>No payments yet</h3>
          <p>Your payment history will appear here.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {payments.map(p => (
            <div key={p._id} className="card">
              <div style={{ padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span className="font-mono" style={{ fontSize: '.78rem', color: 'var(--text-muted)' }}>{p.receiptNumber}</span>
                    <span className="badge badge-success">Paid</span>
                  </div>
                  <div style={{ fontWeight: 600, textTransform: 'capitalize' }}>{p.paymentFor?.replace(/_/g, ' ')}</div>
                  {p.planSnapshot?.name && (
                    <div style={{ fontSize: '.8rem', color: 'var(--text-muted)' }}>Plan: {p.planSnapshot.name}</div>
                  )}
                  <div style={{ fontSize: '.8rem', color: 'var(--text-muted)', marginTop: 4 }}>
                    {format(new Date(p.paymentDate), 'dd MMM yyyy HH:mm')} · via {p.paymentMethod?.replace('_', ' ').toUpperCase()}
                    {p.transactionId && <span style={{ marginLeft: 6, fontFamily: 'monospace' }}>· {p.transactionId}</span>}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--success)' }}>₹{fmt(p.totalAmount)}</div>
                  <a href={`/api/v1/payments/${p._id}/receipt`} target="_blank" rel="noreferrer"
                    className="btn btn-secondary btn-sm" style={{ marginTop: 6 }}>
                    <Printer size={12} /> Receipt
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 24, padding: 14, background: 'var(--bg)', borderRadius: 8, fontSize: '.82rem', color: 'var(--text-muted)', textAlign: 'center' }}>
        For billing queries, contact your ISP office or raise a support ticket.
      </div>
    </div>
  );
}
